import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-toastify";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";

const useAdd = (
  queryKey,
  token = null, // Default token to null
  successMessage,
  errorMessage
) => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [resetForm, setResetForm] = useState(false);
  const [responseData, setResponseData] = useState("");
  const { t } = useTranslation();
  const getResourceKey = () => {
    const parts = String(queryKey || "").split("/").filter(Boolean);
    return parts[parts.length - 1] || queryKey;
  };
  const getResourceKeyVariants = () => {
    const resourceKey = String(getResourceKey() || "").trim();
    if (!resourceKey) return [];

    const variants = new Set([resourceKey]);

    if (resourceKey.endsWith("ies")) {
      variants.add(resourceKey.slice(0, -3) + "y");
    } else if (resourceKey.endsWith("s")) {
      variants.add(resourceKey.slice(0, -1));
    } else {
      variants.add(`${resourceKey}s`);
    }

    return Array.from(variants);
  };

  const getRelatedInvalidationKeys = () => {
    const resourceKey = String(getResourceKey() || "").trim();
    const relatedKeys = new Set(getResourceKeyVariants());

    if (resourceKey === "purchase-payments") {
      relatedKeys.add("purchase");
      relatedKeys.add("supplier");
      relatedKeys.add("purchase_payment");
      relatedKeys.add("agent-payment-list-suppliers");
      relatedKeys.add("agent-payment-list-customers");
    } else if (resourceKey === "account-transactions") {
      relatedKeys.add("account_transaction");
      relatedKeys.add("agent-payment-list-suppliers");
    }

    return Array.from(relatedKeys);
  };

  const addMutation = useMutation({
    mutationFn: async (data) => {
      setLoading(true);

      // Initialize headers object
      const headers = auth?.buildAuthHeader?.() || {};

      // Add Authorization header only if the token is valid
      if (
        !headers.Authorization &&
        token &&
        typeof token === "string" &&
        token.trim().length > 0
      ) {
        headers.Authorization = `Token ${token.trim()}`;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/${queryKey}/`,
        data,
        { headers }
      );
      setResponseData(response.data);
      return response.data;
    },
    onSuccess: () => {
      setLoading(false);
      toast.success(successMessage || t("api.addSuccess"), {
        position: "top-right",
        autoClose: 3000,
      });
      setResetForm(true);

      // Reset the form after a short delay
      setTimeout(() => {
        setResetForm(false);
      }, 100);

      // Invalidate the query to refresh the data
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = String(query.queryKey?.[0] || "");
          return getRelatedInvalidationKeys().includes(key);
        },
      });
    },
    onError: (error) => {
      setLoading(false);

      // Handle specific backend errors if available
      const backendErrors = error.response?.data;
      if (backendErrors && typeof backendErrors === "object") {
        Object.keys(backendErrors).forEach((key) => {
          const errorMsg = Array.isArray(backendErrors[key])
            ? backendErrors[key].join(", ")
            : backendErrors[key];
          toast.error(`${key}: ${errorMsg}`, {
            position: "top-right",
            autoClose: 3000,
          });
        });
      } else {
        // If no specific backend error, show generic error message
        toast.error(
          errorMessage ||
            t("api.failedAdd") ||
            `${t("api.error")}: ${error.message}`,
          {
            position: "top-right",
            autoClose: 3000,
          }
        );
      }
    },
  });

  const handleAdd = (data) => {
    addMutation.mutate(data);
  };

  const handleAddAsync = async (data) => {
    return await addMutation.mutateAsync(data);
  };

  return {
    handleAdd,
    handleAddAsync,
    resetForm,
    loading,
    responseData,
  };
};

export default useAdd;
